import type { ColumnDef } from '@tanstack/react-table'
import {
  CheckCircle,
  Clock,
  Crown,
  Edit,
  Eye,
  Mail,
  MoreHorizontal,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  UserCheck,
  Users,
  UserX,
} from 'lucide-react'
import { useState } from 'react'
import { EnhancedTable } from '@/shared/components/table/EnhancedTable'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { Input } from '@/shared/components/ui/input'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import { teamService } from '../services/teamService'
import type { TeamMember } from '../types/team.types'

interface TeamMembersTableProps {
  teamMembers: TeamMember[]
  loading?: boolean
  onTeamMemberUpdated?: () => void
  onTeamMemberRemoved?: () => void
}

export function TeamMembersTable({
  teamMembers,

  onTeamMemberUpdated,
  onTeamMemberRemoved,
}: TeamMembersTableProps) {
  const [updatingMember, setUpdatingMember] = useState<string | null>(null)
  const [removingMember, setRemovingMember] = useState<string | null>(null)
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false)
  const [memberToRemove, setMemberToRemove] = useState<TeamMember | null>(null)
  const [searchTerm, setSearchTerm] = useState('')

  const handleUpdateRole = async (
    member: TeamMember,
    newRole: TeamMember['role']
  ) => {
    if (member.role === newRole) return

    setUpdatingMember(member.id)
    try {
      await teamService.updateTeamMember(member.id, { role: newRole })
      onTeamMemberUpdated?.()
    } catch (error) {
      toast.error(extractErrorMessage(error))
    } finally {
      setUpdatingMember(null)
    }
  }

  const handleUpdateStatus = async (
    member: TeamMember,
    newStatus: TeamMember['status']
  ) => {
    if (member.status === newStatus) return

    setUpdatingMember(member.id)
    try {
      await teamService.updateTeamMember(member.id, { status: newStatus })
      onTeamMemberUpdated?.()
    } catch (error) {
      toast.error(extractErrorMessage(error))
    } finally {
      setUpdatingMember(null)
    }
  }

  const handleResendInvite = async (member: TeamMember) => {
    setUpdatingMember(member.id)
    try {
      await teamService.resendInvite(member.id)
    } catch (error) {
      toast.error(extractErrorMessage(error))
    } finally {
      setUpdatingMember(null)
    }
  }

  const handleRemoveMember = async () => {
    if (!memberToRemove) return

    setRemovingMember(memberToRemove.id)
    try {
      await teamService.removeTeamMember(memberToRemove.id)
      setConfirmRemoveOpen(false)
      setMemberToRemove(null)
      onTeamMemberRemoved?.()
    } catch (error) {
      toast.error(extractErrorMessage(error))
    } finally {
      setRemovingMember(null)
    }
  }

  const getRoleIcon = (role: TeamMember['role']) => {
    switch (role) {
      case 'owner':
        return <ShieldCheck className='w-4 h-4 text-purple-600' />
      case 'admin':
        return <Crown className='w-4 h-4 text-amber-500' />
      case 'member':
        return <Users className='w-4 h-4 text-blue-500' />
      case 'viewer':
        return <Eye className='w-4 h-4 text-slate-500' />
    }
  }

  const getRoleVariant = (role: TeamMember['role']) => {
    switch (role) {
      case 'owner':
        return 'default' as const
      case 'admin':
        return 'default' as const
      case 'member':
        return 'secondary' as const
      case 'viewer':
        return 'outline' as const
    }
  }

  const getStatusVariant = (status: TeamMember['status']) => {
    switch (status) {
      case 'active':
        return 'default' as const
      case 'pending':
        return 'secondary' as const
      case 'inactive':
        return 'destructive' as const
    }
  }

  const getStatusIcon = (status: TeamMember['status']) => {
    switch (status) {
      case 'active':
        return <CheckCircle className='w-4 h-4' />
      case 'pending':
        return <Clock className='w-4 h-4' />
      case 'inactive':
        return <UserX className='w-4 h-4' />
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const getLastActiveText = (member: TeamMember) => {
    if (!member.last_active) {
      return member.status === 'pending' ? 'Never logged in' : 'Never'
    }

    const lastActive = new Date(member.last_active)
    const now = new Date()
    const diffDays = Math.floor(
      (now.getTime() - lastActive.getTime()) / (1000 * 60 * 60 * 24)
    )

    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays} days ago`
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
    return `${Math.floor(diffDays / 30)} months ago`
  }

  const columns: ColumnDef<TeamMember>[] = [
    {
      accessorKey: 'name',
      header: 'Team Member',
      cell: ({ row }) => {
        const member = row.original
        return (
          <div className='flex items-center gap-3'>
            <div className='w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-medium'>
              {member.name.charAt(0).toUpperCase()}
            </div>
            <div className='min-w-0'>
              <div className='font-medium truncate'>{member.name}</div>
              <div className='text-sm text-muted-foreground truncate'>
                {member.email}
              </div>
            </div>
          </div>
        )
      },
      size: 300,
    },
    {
      accessorKey: 'role',
      header: 'Role',
      cell: ({ row }) => {
        const member = row.original
        return (
          <div className='flex items-center gap-2'>
            {getRoleIcon(member.role)}
            <Badge variant={getRoleVariant(member.role)}>
              {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
            </Badge>
          </div>
        )
      },
      size: 150,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const member = row.original
        return (
          <div className='flex items-center gap-2'>
            {getStatusIcon(member.status)}
            <Badge variant={getStatusVariant(member.status)}>
              {member.status.charAt(0).toUpperCase() + member.status.slice(1)}
            </Badge>
          </div>
        )
      },
      size: 150,
    },
    {
      accessorKey: 'joined_at',
      header: 'Joined',
      cell: ({ row }) => {
        const member = row.original
        return (
          <div className='text-sm'>
            <div>{formatDate(member.joined_at)}</div>
            <div className='text-xs text-muted-foreground'>
              {getLastActiveText(member)}
            </div>
          </div>
        )
      },
      size: 150,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const member = row.original
        const isCurrentUser = member.email === 'you@company.com'
        const isUpdating = updatingMember === member.id

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant='ghost'
                className='h-8 w-8 p-0'
                disabled={isUpdating}
              >
                <span className='sr-only'>Open menu</span>
                {isUpdating ? (
                  <RefreshCw className='h-4 w-4 animate-spin' />
                ) : (
                  <MoreHorizontal className='h-4 w-4' />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              {/* Edit Role Section */}
              {!isCurrentUser && (
                <>
                  <DropdownMenuItem
                    disabled
                    className='text-xs font-medium text-muted-foreground cursor-default'
                  >
                    <Edit className='mr-2 h-3 w-3' />
                    Edit Role
                  </DropdownMenuItem>

                  {/* Owner can be downgraded to Admin or Member */}
                  {member.role === 'owner' && (
                    <>
                      <DropdownMenuItem
                        onClick={() => handleUpdateRole(member, 'admin')}
                      >
                        <Crown className='mr-2 h-4 w-4 text-amber-500' />
                        Downgrade to Admin
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleUpdateRole(member, 'member')}
                      >
                        <Users className='mr-2 h-4 w-4 text-blue-500' />
                        Downgrade to Member
                      </DropdownMenuItem>
                    </>
                  )}

                  {/* Admin can be upgraded to Owner or downgraded to Member/Viewer */}
                  {member.role === 'admin' && (
                    <>
                      <DropdownMenuItem
                        onClick={() => handleUpdateRole(member, 'owner')}
                      >
                        <ShieldCheck className='mr-2 h-4 w-4 text-purple-600' />
                        Promote to Owner
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleUpdateRole(member, 'member')}
                      >
                        <Users className='mr-2 h-4 w-4 text-blue-500' />
                        Downgrade to Member
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleUpdateRole(member, 'viewer')}
                      >
                        <Eye className='mr-2 h-4 w-4 text-slate-500' />
                        Downgrade to Viewer
                      </DropdownMenuItem>
                    </>
                  )}

                  {/* Member can be upgraded to Admin/Owner or downgraded to Viewer */}
                  {member.role === 'member' && (
                    <>
                      <DropdownMenuItem
                        onClick={() => handleUpdateRole(member, 'owner')}
                      >
                        <ShieldCheck className='mr-2 h-4 w-4 text-purple-600' />
                        Promote to Owner
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleUpdateRole(member, 'admin')}
                      >
                        <Crown className='mr-2 h-4 w-4 text-amber-500' />
                        Promote to Admin
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleUpdateRole(member, 'viewer')}
                      >
                        <Eye className='mr-2 h-4 w-4 text-slate-500' />
                        Downgrade to Viewer
                      </DropdownMenuItem>
                    </>
                  )}

                  {/* Viewer can only be upgraded */}
                  {member.role === 'viewer' && (
                    <>
                      <DropdownMenuItem
                        onClick={() => handleUpdateRole(member, 'owner')}
                      >
                        <ShieldCheck className='mr-2 h-4 w-4 text-purple-600' />
                        Promote to Owner
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleUpdateRole(member, 'admin')}
                      >
                        <Crown className='mr-2 h-4 w-4 text-amber-500' />
                        Promote to Admin
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleUpdateRole(member, 'member')}
                      >
                        <Users className='mr-2 h-4 w-4 text-blue-500' />
                        Promote to Member
                      </DropdownMenuItem>
                    </>
                  )}
                </>
              )}

              {/* Resend Invite */}
              {member.status === 'pending' && (
                <DropdownMenuItem onClick={() => handleResendInvite(member)}>
                  <Mail className='mr-2 h-4 w-4' />
                  Resend Invite
                </DropdownMenuItem>
              )}

              {/* Status Management */}
              {!isCurrentUser && member.status === 'active' && (
                <DropdownMenuItem
                  onClick={() => handleUpdateStatus(member, 'inactive')}
                >
                  <UserX className='mr-2 h-4 w-4' />
                  Deactivate
                </DropdownMenuItem>
              )}

              {!isCurrentUser && member.status === 'inactive' && (
                <DropdownMenuItem
                  onClick={() => handleUpdateStatus(member, 'active')}
                >
                  <UserCheck className='mr-2 h-4 w-4' />
                  Reactivate
                </DropdownMenuItem>
              )}

              {/* Remove Member */}
              {!isCurrentUser && (
                <DropdownMenuItem
                  onClick={() => {
                    setMemberToRemove(member)
                    setConfirmRemoveOpen(true)
                  }}
                  className='text-red-600'
                >
                  <Trash2 className='mr-2 h-4 w-4' />
                  Remove
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
      size: 60,
    },
  ]

  // Filter team members based on search term
  const filteredTeamMembers = teamMembers.filter(member => {
    if (!searchTerm) return true
    const searchLower = searchTerm.toLowerCase()
    return (
      member.name.toLowerCase().includes(searchLower) ||
      member.email.toLowerCase().includes(searchLower)
    )
  })

  return (
    <>
      {/* Search Bar */}
      <div className='mb-4'>
        <div className='relative w-full max-w-sm'>
          <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4' />
          <Input
            placeholder='Search team members...'
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className='pl-10'
          />
        </div>
      </div>

      <div className='[&_table]:bg-transparent [&_thead]:bg-transparent [&_tr:hover]:bg-slate-50/50 dark:[&_tr:hover]:bg-slate-800/50'>
        <EnhancedTable
          key='team-members-table'
          data={filteredTeamMembers}
          columns={columns}
          height='calc(100vh - 450px)'
          className='bg-transparent border-0'
        />
      </div>

      {/* Remove Confirmation Dialog */}
      <Dialog open={confirmRemoveOpen} onOpenChange={setConfirmRemoveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Team Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove "{memberToRemove?.name}" from your
              team? This action cannot be undone and they will lose access to
              your organization.
            </DialogDescription>
          </DialogHeader>
          <div className='flex justify-end gap-3 mt-6'>
            <Button
              variant='outline'
              onClick={() => {
                setConfirmRemoveOpen(false)
                setMemberToRemove(null)
              }}
              disabled={removingMember !== null}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRemoveMember}
              className='bg-red-600 hover:bg-red-700 focus:ring-red-600'
              disabled={removingMember !== null}
            >
              {removingMember ? (
                <>
                  <RefreshCw className='mr-2 h-4 w-4 animate-spin' />
                  Removing...
                </>
              ) : (
                'Remove Member'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
